import { Button, MagellanPopup } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Form, Formik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiYoutubeLogo } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetMagellanAccountsLightList } from 'services/accounts';
import {
  usePostYoutubeRefreshToken,
  usePostYoutubeRefreshTokenAll,
} from 'services/youtube';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface IPopupYoutubeMagelupAuthorizeProps {
  trigger?: JSX.Element;
  onSuccess?: () => void;
  adminAuthorized?: boolean;
}

type AuthorizeStep = 'choose' | 'single';

interface ISingleAccountForm {
  magellanAccountId: string;
}

const PopupYoutubeMagelupAuthorize = ({
  trigger,
  onSuccess,
  adminAuthorized = false,
}: IPopupYoutubeMagelupAuthorizeProps) => {
  const popupRef = useRef<PopupActions>(null);
  const [step, setStep] = useState<AuthorizeStep>('choose');
  const { t } = useTranslation();

  const accounts = useGetMagellanAccountsLightList();
  const refreshAll = usePostYoutubeRefreshTokenAll();
  const refreshOne = usePostYoutubeRefreshToken();

  const accountOptions = useMemo(
    () =>
      accounts.data?.map(account => ({
        value: account.id,
        label: account.accountName,
      })) ?? [],
    [accounts.data]
  );

  const onClose = () => {
    setStep('choose');
  };

  const closeAndRefresh = () => {
    popupRef.current?.close();
    onSuccess?.();
  };

  const handleRefreshAll = async () => {
    await refreshAll.mutateAsync(undefined, {
      onSuccess: closeAndRefresh,
    });
  };

  const handleRefreshSingle = async (magellanAccountId: string) => {
    if (!magellanAccountId) {
      return;
    }

    await refreshOne.mutateAsync(magellanAccountId, {
      onSuccess: closeAndRefresh,
    });
  };

  const isPending = refreshAll.isPending || refreshOne.isPending;

  return (
    <Popup ref={popupRef} modal nested trigger={trigger} onClose={onClose}>
      <MagellanPopup className='p-5 rounded-md'>
        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiYoutubeLogo /> {t('youtubeOAuth.popup.magelup.title')}
        </MagellanPopup.Header>

        {adminAuthorized && step === 'choose' && (
          <div className='flex flex-col gap-3 w-80'>
            <p className='text-center opacity-70'>
              {t('youtubeOAuth.popup.magelup.message')}
            </p>
            <Button
              type='button'
              fullWidth
              variant={BUTTON_VARIANT.submit}
              disabled={isPending}
              isLoading={refreshAll.isPending}
              onClick={handleRefreshAll}>
              <Button.Text text={t('youtubeOAuth.popup.magelup.all')} />
            </Button>
            <Button
              type='button'
              fullWidth
              variant={BUTTON_VARIANT.default}
              disabled={isPending}
              onClick={() => setStep('single')}>
              <Button.Text text={t('youtubeOAuth.popup.magelup.single')} />
            </Button>
          </div>
        )}

        {adminAuthorized && step === 'single' && (
          <Formik<ISingleAccountForm>
            initialValues={{ magellanAccountId: '' }}
            onSubmit={values => handleRefreshSingle(values.magellanAccountId)}>
            {({ submitForm, values }) => (
              <Form className='flex flex-col gap-3 w-80'>
                <FormInput.Select
                  name='magellanAccountId'
                  label={t('input.label.magellanAccount')}
                  options={accountOptions}
                  isAsync={accounts.isLoading}
                />
                <MagellanPopup.BottomButtons>
                  <Button
                    type='button'
                    isClear
                    onClick={() => setStep('choose')}
                    disabled={isPending}>
                    <Button.Text text={t('youtubeOAuth.popup.magelup.back')} />
                  </Button>
                  <Button
                    type='button'
                    variant={BUTTON_VARIANT.submit}
                    disabled={!values.magellanAccountId || isPending}
                    isLoading={refreshOne.isPending}
                    onClick={submitForm}>
                    <Button.Text text={t('popup.approve')} />
                    <Button.Icon icon={<PiYoutubeLogo />} />
                  </Button>
                </MagellanPopup.BottomButtons>
              </Form>
            )}
          </Formik>
        )}
      </MagellanPopup>
    </Popup>
  );
};

export default PopupYoutubeMagelupAuthorize;
